import moment, { Moment } from "moment";
import { ICalendarWorkTemplateBase, WorkOrderStatus } from "@miplatform/types";
import { getDate, getDateISO, setTime } from "@miplatform/utils";
import { DragEndEvent } from "@dnd-kit/core";
import {
  IPrioritySchedulerColumnData,
  PrioritySchedulerAffectedCard,
  PrioritySchedulerAfterCardDropPayload,
  PrioritySchedulerCardDragData,
  PrioritySchedulerColumnEvent,
  PrioritySchedulerContainerDragData,
  PrioritySchedulerLocation,
  PrioritySchedulerMovePayload,
} from "./PrioritySchedulerBoard.types";
import {
  type TimeWindowRange,
  momentToClockMinutes,
} from "./autoAssignWindow.utils";
import {
  type DisabledSlotRect,
  PrioritySchedulerEventLayout,
  buildDisabledSlotRects,
  computeEventLayouts,
  snapPastDisabledSlots,
} from "./eventLayouts.utils";

export type { TimeWindowRange } from "./autoAssignWindow.utils";
export { layoutIntervalFitsAnyTemplateWindow } from "./autoAssignWindow.utils";

const CARD_PREFIX = "card";

export const COLUMN_CONTAINER_PREFIX = "column";
export const TRAY_CONTAINER_PREFIX = "tray";
export const COLUMN_DROP_STACK_GAP_PX = 0;

// TODO: ABK this is a temporary value, we might not need it.
// for now we expect that some data does not have a duration.
export const DEFAULT_EVENT_DURATION_MINUTES = 60;

export const getCardDnDId = (id: string) => `${CARD_PREFIX}:${id}`;

const NON_DRAGGABLE_WORK_ORDER_STATUSES = new Set<string>([
  WorkOrderStatus.InProgress,
  WorkOrderStatus.Complete,
  WorkOrderStatus.Hold,
]);

export const isWorkOrderDragDisabled = (
  event?: PrioritySchedulerColumnEvent
): boolean => {
  const status = event?.workOrderStatus ?? event?.orderStatus;
  const isStatusDragDisabled = status
    ? NON_DRAGGABLE_WORK_ORDER_STATUSES.has(status)
    : false;
  return isStatusDragDisabled || !!event?.assignment?.workOrder?.isLocked;
};

export const getRawId = (value: string) => value.split(":").slice(1).join(":");

export const getContainerDnDId = (type: "column" | "tray", id: string) =>
  `${
    type === "column" ? COLUMN_CONTAINER_PREFIX : TRAY_CONTAINER_PREFIX
  }:${id}`;

export const moveCardBetweenColumns = (
  columns: IPrioritySchedulerColumnData[],
  fromColumnId: string,
  toColumnId: string,
  cardId: string
): {
  next: IPrioritySchedulerColumnData[];
  moved?: PrioritySchedulerColumnEvent;
} => {
  let movedCard: PrioritySchedulerColumnEvent | undefined;

  const withoutCard = columns.map((column) => {
    if (column.id !== fromColumnId) return column;
    const events = column.events ?? [];
    const found = events.find((event) => event.id === cardId);
    if (found) movedCard = found;
    return {
      ...column,
      events: events.filter((event) => event.id !== cardId),
    };
  });

  if (!movedCard) return { next: columns };

  const next = withoutCard.map((column) => {
    if (column.id !== toColumnId) return column;
    const events = column.events ?? [];
    return {
      ...column,
      events: [...events, movedCard as PrioritySchedulerColumnEvent],
    };
  });

  return { next, moved: movedCard };
};

export const addCardToColumn = (
  columns: IPrioritySchedulerColumnData[],
  toColumnId: string,
  card: PrioritySchedulerColumnEvent
): {
  next: IPrioritySchedulerColumnData[];
  added: PrioritySchedulerColumnEvent;
} | null => {
  const destColumn = columns.find((column) => column.id === toColumnId);
  if (!destColumn) return null;

  const events = destColumn.events ?? [];
  if (events.some((event) => event.id === card.id)) return null;

  const next = columns.map((column) =>
    column.id !== toColumnId ? column : { ...column, events: [...events, card] }
  );

  return { next, added: card };
};

export const sortColumnEventsBySchedule = (
  columns: IPrioritySchedulerColumnData[],
  columnIds: Set<string>
): IPrioritySchedulerColumnData[] =>
  columns.map((col) => {
    if (!columnIds.has(col.id)) return col;
    const events = [...(col.events ?? [])].sort((a, b) => {
      const ta = a.scheduledStartDate
        ? getDate(a.scheduledStartDate).valueOf()
        : Number.MAX_SAFE_INTEGER;
      const tb = b.scheduledStartDate
        ? getDate(b.scheduledStartDate).valueOf()
        : Number.MAX_SAFE_INTEGER;
      if (ta !== tb) return ta - tb;
      return 0;
    });
    return { ...col, events };
  });

export const parseWindowTimeToMinutes = (time: string): number => {
  const [hours = 0, minutes = 0] = time.split(":").map(Number);
  return hours * 60 + minutes;
};

export const buildTemplateWindowsById = (
  workTemplateBases: ICalendarWorkTemplateBase[]
): Record<string, TimeWindowRange[]> =>
  workTemplateBases.reduce<Record<string, TimeWindowRange[]>>(
    (acc, template) => {
      if (!template.autoAssignWindows.length) {
        return acc;
      }
      const windows = template.autoAssignWindows
        .map((window) => ({
          startMinutes: parseWindowTimeToMinutes(window.start),
          endMinutes: parseWindowTimeToMinutes(window.end),
        }))
        .filter((window) => window.endMinutes > window.startMinutes)
        .sort((a, b) => a.startMinutes - b.startMinutes);
      if (!windows.length) {
        return acc;
      }
      acc[template.id] = windows;
      return acc;
    },
    {}
  );

const getNowMinutesFromMidnight = (now: Moment): number =>
  now.hours() * 60 + now.minutes();

export const getEffectiveWindows = (
  windows: TimeWindowRange[],
  day: Moment,
  now: Moment = moment()
): TimeWindowRange[] => {
  if (!day.isSame(now, "day")) {
    return windows;
  }
  const nowMinutes = getNowMinutesFromMidnight(now);

  return windows
    .map((w) => ({
      startMinutes: Math.max(w.startMinutes, nowMinutes),
      endMinutes: w.endMinutes,
    }))
    .filter((w) => w.endMinutes > w.startMinutes);
};

export const eventOverlapsWindow = (
  interval: { start: Moment; end: Moment },
  window: TimeWindowRange
): boolean => {
  const eventStart = momentToClockMinutes(interval.start);
  const eventEnd = momentToClockMinutes(interval.end);
  return eventStart < window.endMinutes && eventEnd > window.startMinutes;
};

export const pickTemplateWindowForPointer = (
  windows: TimeWindowRange[],
  pointerClockMinutes: number
): TimeWindowRange => {
  const containing = windows.find(
    (w) =>
      pointerClockMinutes >= w.startMinutes &&
      pointerClockMinutes < w.endMinutes
  );
  return containing ?? windows[0];
};

export const windowIntersectsClockTime = (
  window: TimeWindowRange,
  clockMinutes: number
): boolean =>
  window.startMinutes < clockMinutes && window.endMinutes > clockMinutes;

export const getPresentEventsInWindow = (
  columnEvents: PrioritySchedulerColumnEvent[],
  window: TimeWindowRange,
  nowClockMinutes: number | null
): PrioritySchedulerColumnEvent[] =>
  columnEvents.filter((event) => {
    const interval = getEventScheduledInterval(event);
    if (!interval || !eventOverlapsWindow(interval, window)) {
      return false;
    }
    if (nowClockMinutes != null) {
      return momentToClockMinutes(interval.end) > nowClockMinutes;
    }
    return true;
  });

const getPointerClockMinutes = ({
  columnDate,
  dayStartTime,
  pointerOffsetPx,
  hourHeight,
}: {
  columnDate: Moment;
  dayStartTime: string | undefined;
  pointerOffsetPx: number;
  hourHeight: number;
}): number => {
  const columnDayStart = dayStartTime
    ? setTime(columnDate, dayStartTime)
    : getDate(columnDate).startOf("day");
  const pointerMinutesFromDayStart = (pointerOffsetPx / hourHeight) * 60;
  return momentToClockMinutes(
    columnDayStart.clone().add(pointerMinutesFromDayStart, "minutes")
  );
};

const resolveEffectiveWindowForPointer = ({
  templateWindows,
  pointerClockMinutes,
  columnDate,
  now,
  allEffective,
}: {
  templateWindows: TimeWindowRange[];
  pointerClockMinutes: number;
  columnDate: Moment;
  now: Moment;
  allEffective: TimeWindowRange[];
}): {
  fullWindow: TimeWindowRange;
  effectiveWindow: TimeWindowRange;
} | null => {
  const fullWindow = pickTemplateWindowForPointer(
    templateWindows,
    pointerClockMinutes
  );
  const [effectiveFromPointer] = getEffectiveWindows(
    [fullWindow],
    columnDate,
    now
  );
  const effectiveWindow = effectiveFromPointer ?? allEffective[0];
  if (!effectiveWindow) {
    return null;
  }
  return { fullWindow, effectiveWindow };
};

const getLatestPresentEventEndMinutes = (
  presentEvents: PrioritySchedulerColumnEvent[],
  fallbackMinutes: number
): number =>
  presentEvents.reduce((maxEnd, event) => {
    const interval = getEventScheduledInterval(event);
    if (!interval) {
      return maxEnd;
    }
    return Math.max(maxEnd, momentToClockMinutes(interval.end));
  }, fallbackMinutes);

const computeDropCursorMinutes = ({
  effectiveWindow,
  presentEvents,
  windowIntersectsNow,
  nowClockMinutes,
  hourHeight,
}: {
  effectiveWindow: TimeWindowRange;
  presentEvents: PrioritySchedulerColumnEvent[];
  windowIntersectsNow: boolean;
  nowClockMinutes: number | null;
  hourHeight: number;
}): number => {
  if (presentEvents.length === 0) {
    return windowIntersectsNow && nowClockMinutes != null
      ? nowClockMinutes
      : effectiveWindow.startMinutes;
  }

  const lastEndMinutes = getLatestPresentEventEndMinutes(
    presentEvents,
    effectiveWindow.startMinutes
  );
  const stackGapMinutes = (COLUMN_DROP_STACK_GAP_PX / hourHeight) * 60;
  let cursorMinutes = Math.max(effectiveWindow.startMinutes, lastEndMinutes);
  if (lastEndMinutes > effectiveWindow.startMinutes) {
    cursorMinutes += stackGapMinutes;
  }
  if (windowIntersectsNow && nowClockMinutes != null) {
    cursorMinutes = Math.max(cursorMinutes, nowClockMinutes);
  }
  return cursorMinutes;
};

/**
 * Resolves the scheduled start time for a drop into a column with auto-assign
 * template windows. Uses pointerOffsetPx only to pick which window applies,
 * not as the schedule time: the card is placed at the next open slot inside
 * that window—after existing events (stacked with COLUMN_DROP_STACK_GAP_PX),
 * at window start, or at now when the window spans the current time on today.
 * Windows are clipped to now on the column day. Returns null when no effective
 * window remains or expectedDurationMinutes does not fit before window end.
 */
export const resolveTemplateWindowDropStart = ({
  columnDate,
  dayStartTime,
  pointerOffsetPx,
  hourHeight,
  templateWindows,
  columnEvents,
  expectedDurationMinutes,
  now = moment(),
}: {
  columnDate: Moment;
  dayStartTime: string | undefined;
  pointerOffsetPx: number;
  hourHeight: number;
  templateWindows: TimeWindowRange[];
  columnEvents: PrioritySchedulerColumnEvent[];
  expectedDurationMinutes: number;
  now?: Moment;
}): Moment | null => {
  const allEffective = getEffectiveWindows(templateWindows, columnDate, now);
  if (!allEffective.length) {
    return null;
  }

  const resolvedWindow = resolveEffectiveWindowForPointer({
    templateWindows,
    pointerClockMinutes: getPointerClockMinutes({
      columnDate,
      dayStartTime,
      pointerOffsetPx,
      hourHeight,
    }),
    columnDate,
    now,
    allEffective,
  });
  if (!resolvedWindow) {
    return null;
  }

  const { fullWindow, effectiveWindow } = resolvedWindow;
  const nowClockMinutes = columnDate.isSame(now, "day")
    ? getNowMinutesFromMidnight(now)
    : null;
  const cursorMinutes = computeDropCursorMinutes({
    effectiveWindow,
    presentEvents: getPresentEventsInWindow(
      columnEvents,
      fullWindow,
      nowClockMinutes
    ),
    windowIntersectsNow:
      nowClockMinutes != null &&
      windowIntersectsClockTime(fullWindow, nowClockMinutes),
    nowClockMinutes,
    hourHeight,
  });

  return getDate(columnDate).startOf("day").add(cursorMinutes, "minutes");
};

type ResolveSnappedColumnDropOffsetYParams = {
  rawY: number;
  destColumn: IPrioritySchedulerColumnData | undefined;
  draggedCardId: string;
  excludeDraggedCard: boolean;
  calculatedHourHeight: number;
  dayStartTime: string | undefined;
  dayEndTime: string | undefined;
  fallbackDate: Moment;
  workTemplateBases?: ICalendarWorkTemplateBase[];
  draggedWorkOrderTemplateId?: string;
  expectedDurationMinutes?: number;
  now?: Moment;
};

export const resolveSnappedColumnDropOffsetY = ({
  rawY,
  destColumn,
  draggedCardId,
  excludeDraggedCard,
  calculatedHourHeight,
  dayStartTime,
  dayEndTime,
  fallbackDate,
  workTemplateBases,
  draggedWorkOrderTemplateId,
  expectedDurationMinutes,
  now = moment(),
}: ResolveSnappedColumnDropOffsetYParams): number | null => {
  const columnDate = destColumn?.date ?? fallbackDate;
  const duration = expectedDurationMinutes ?? DEFAULT_EVENT_DURATION_MINUTES;
  const templateWindows =
    draggedWorkOrderTemplateId && workTemplateBases?.length
      ? buildTemplateWindowsById(workTemplateBases)[draggedWorkOrderTemplateId]
      : undefined;

  if (templateWindows?.length) {
    const eventsForPlacement = (destColumn?.events ?? []).filter(
      (event) => !excludeDraggedCard || event.id !== draggedCardId
    );
    const dropStart = resolveTemplateWindowDropStart({
      columnDate,
      dayStartTime,
      pointerOffsetPx: rawY,
      hourHeight: calculatedHourHeight,
      templateWindows,
      columnEvents: eventsForPlacement,
      expectedDurationMinutes: duration,
      now,
    });
    if (!dropStart) {
      return null;
    }

    const columnDayStart = dayStartTime
      ? setTime(columnDate, dayStartTime)
      : getDate(columnDate).startOf("day");
    const disabledSlotRects = buildDisabledSlotRects(
      destColumn?.disabledSlots,
      columnDayStart,
      calculatedHourHeight
    );

    const nowClockMinutes = columnDate.isSame(now, "day")
      ? getNowMinutesFromMidnight(now)
      : null;
    const dropOffsetPx =
      nowClockMinutes != null &&
      momentToClockMinutes(dropStart) === nowClockMinutes
        ? getCurrentTimeOffsetPx({
            columnDate,
            dayStartTime,
            hourHeight: calculatedHourHeight,
            now,
          })
        : momentToOffsetPx(dropStart, columnDayStart, calculatedHourHeight);

    return snapPastDisabledSlots(dropOffsetPx, disabledSlotRects);
  }

  const eventsForLayout = (destColumn?.events ?? []).filter(
    (event) => !excludeDraggedCard || event.id !== draggedCardId
  );
  const templateWindowsById =
    getTemplateWindowsByIdForCalendar(workTemplateBases);
  const columnDayStart = dayStartTime
    ? setTime(columnDate, dayStartTime)
    : getDate(columnDate).startOf("day");
  const disabledSlotRects = buildDisabledSlotRects(
    destColumn?.disabledSlots,
    columnDayStart,
    calculatedHourHeight
  );
  const { layouts } = computeEventLayouts({
    events: eventsForLayout,
    hourHeight: calculatedHourHeight,
    columnDate,
    dayStartTime,
    dayEndTime,
    stackGapPx: COLUMN_DROP_STACK_GAP_PX,
    templateWindowsById,
    disabledSlots: destColumn?.disabledSlots,
    now,
  });
  const sortedLayouts = [...layouts.values()].sort((a, b) => a.top - b.top);
  const currentTimeOffsetPx = getCurrentTimeOffsetPx({
    columnDate,
    dayStartTime,
    hourHeight: calculatedHourHeight,
  });
  const snapPositions = getColumnSnapPositions(
    sortedLayouts,
    currentTimeOffsetPx,
    disabledSlotRects
  ).map((position) => Math.round(position));

  return snapPastDisabledSlots(
    resolveSnappedDropY(rawY, snapPositions),
    disabledSlotRects
  );
};

export const computeScheduleFromDropY = ({
  dropOffsetY,
  hourHeight,
  columnDate,
  fallbackDate,
  dayStartTime,
  expectedDurationMinutes,
}: {
  dropOffsetY: number;
  hourHeight: number;
  columnDate: Moment | undefined;
  fallbackDate: Moment;
  dayStartTime: string | undefined;
  expectedDurationMinutes: number;
}): { scheduledStartDate: string; scheduledEndDate: string } | null => {
  const baseDate = columnDate ?? fallbackDate;
  const startMoment = dayStartTime
    ? setTime(baseDate, dayStartTime)
    : getDate(baseDate).startOf("day");

  // dropOffsetY is already snapped to the target slot in pixels; convert back
  // to minutes without re-snapping so the schedule matches the drop indicator.
  const slotMinutes = Math.round((dropOffsetY / hourHeight) * 60);
  const start = startMoment.clone().add(slotMinutes, "minutes");
  const end = start.clone().add(expectedDurationMinutes, "minutes");

  return {
    scheduledStartDate: getDateISO(start),
    scheduledEndDate: getDateISO(end),
  };
};

export const getEventScheduledInterval = (
  event: PrioritySchedulerColumnEvent
): { start: Moment; end: Moment } | null => {
  if (!event.scheduledStartDate) return null;
  const start = getDate(event.scheduledStartDate);
  const duration = event.expectedDuration ?? DEFAULT_EVENT_DURATION_MINUTES;
  const end = event.scheduledEndDate
    ? getDate(event.scheduledEndDate)
    : start.clone().add(duration, "minutes");
  return { start, end };
};

export const intervalsOverlapMoments = (
  aStart: Moment,
  aEnd: Moment,
  bStart: Moment,
  bEnd: Moment
): boolean => aStart.isBefore(bEnd) && aEnd.isAfter(bStart);

/**
 * Returns true when the card's scheduled start falls within at least one
 * autoAssignWindow whose template matches the card's workOrderTemplateId.
 * The card may extend past the window end; only the start must land inside.
 *
 * Permits the drop when: no templateId on the card, no matching template in
 * config, or the matching template has no windows defined.
 */
export const isDropWithinMatchingWindow = ({
  workOrderTemplateId,
  scheduledStart,
  workTemplateBases,
}: {
  workOrderTemplateId: string | undefined;
  scheduledStart: Moment;
  workTemplateBases: ICalendarWorkTemplateBase[];
}): boolean => {
  if (!workOrderTemplateId) return true;
  const template = workTemplateBases.find((t) => t.id === workOrderTemplateId);
  if (!template || template.autoAssignWindows.length === 0) return true;

  const scheduledMinutes = momentToClockMinutes(scheduledStart);
  return template.autoAssignWindows.some((win) => {
    const winStart = parseWindowTimeToMinutes(win.start);
    const winEnd = parseWindowTimeToMinutes(win.end);
    return scheduledMinutes >= winStart && scheduledMinutes < winEnd;
  });
};

export const getTemplateWindowsByIdForCalendar = (
  workTemplateBases: ICalendarWorkTemplateBase[] | undefined
): Record<string, TimeWindowRange[]> | undefined => {
  if (!workTemplateBases?.length) {
    return undefined;
  }
  const templateWindowsById = buildTemplateWindowsById(workTemplateBases);
  return Object.keys(templateWindowsById).length > 0
    ? templateWindowsById
    : undefined;
};

const compareEventsByScheduledStart = (
  a: PrioritySchedulerColumnEvent,
  b: PrioritySchedulerColumnEvent
): number => {
  const ta = a.scheduledStartDate
    ? getDate(a.scheduledStartDate).valueOf()
    : Number.MAX_SAFE_INTEGER;
  const tb = b.scheduledStartDate
    ? getDate(b.scheduledStartDate).valueOf()
    : Number.MAX_SAFE_INTEGER;
  if (ta !== tb) return ta - tb;
  return 0;
};

const isSameTemplateWindow = (
  a: TimeWindowRange,
  b: TimeWindowRange
): boolean =>
  a.startMinutes === b.startMinutes && a.endMinutes === b.endMinutes;

const getBelongingTemplateWindow = (
  workOrderTemplateId: string | undefined,
  scheduledStart: Moment,
  templateWindowsById: Record<string, TimeWindowRange[]>
): TimeWindowRange | null => {
  if (!workOrderTemplateId) return null;
  const windows = templateWindowsById[workOrderTemplateId];
  if (!windows?.length) return null;

  const scheduledMinutes = momentToClockMinutes(scheduledStart);
  return (
    windows.find(
      (window) =>
        scheduledMinutes >= window.startMinutes &&
        scheduledMinutes < window.endMinutes
    ) ?? null
  );
};

const eventBelongsToTemplateWindow = (
  event: PrioritySchedulerColumnEvent,
  window: TimeWindowRange,
  templateWindowsById: Record<string, TimeWindowRange[]>
): boolean => {
  const interval = getEventScheduledInterval(event);
  if (!interval) return false;

  const eventWindow = getBelongingTemplateWindow(
    event.workOrderTemplateId,
    interval.start,
    templateWindowsById
  );

  return (
    eventWindow !== null &&
    event.workOrderTemplateId !== undefined &&
    isSameTemplateWindow(eventWindow, window)
  );
};

const intervalFitsEffectiveTemplateWindow = (
  start: Moment,
  end: Moment,
  window: TimeWindowRange,
  columnDate: Moment,
  now: Moment
): boolean => {
  const [effectiveWindow] = getEffectiveWindows([window], columnDate, now);
  if (!effectiveWindow) return false;

  const startMinutes = momentToClockMinutes(start);
  const endMinutes = momentToClockMinutes(end);
  return (
    startMinutes >= effectiveWindow.startMinutes &&
    endMinutes <= effectiveWindow.endMinutes
  );
};

export type CompactOriginColumnAfterRemovalOptions = {
  columnDate?: Moment;
  workTemplateBases?: ICalendarWorkTemplateBase[];
  now?: Moment;
};

/**
 * Restacks cards in the origin column that were scheduled after a removed card,
 * starting from the removed card's start time so the freed slot is occupied.
 * When the calendar defines template windows, compaction is scoped to the
 * removed card's belonging window only.
 */
export const compactOriginColumnAfterRemoval = (
  events: PrioritySchedulerColumnEvent[],
  removedCard: PrioritySchedulerColumnEvent,
  options?: CompactOriginColumnAfterRemovalOptions
): PrioritySchedulerColumnEvent[] => {
  const removedInterval = getEventScheduledInterval(removedCard);
  // Early exit if the column is empty or the removed card has no scheduled interval
  if (!removedInterval || events.length === 0) return events;

  // Define the gap left by the removed card
  const { start: gapStart, end: gapEnd } = removedInterval;
  const gapEndMs = gapEnd.valueOf();
  const now = options?.now ?? moment();
  const columnDate = options?.columnDate;
  // Get the template windows for the calendar
  const templateWindowsById = getTemplateWindowsByIdForCalendar(
    options?.workTemplateBases
  );
  // Get the belonging template window for the removed card
  const removedWindow =
    templateWindowsById && columnDate
      ? getBelongingTemplateWindow(
          removedCard.workOrderTemplateId,
          removedInterval.start,
          templateWindowsById
        )
      : null;
  // Determine if window-scoped compaction is needed
  const useWindowScopedCompaction =
    templateWindowsById != null && columnDate != null && removedWindow != null;

  const sortedEvents = [...events].sort(compareEventsByScheduledStart);
  const unchanged: PrioritySchedulerColumnEvent[] = [];
  const toCompact: PrioritySchedulerColumnEvent[] = [];

  for (const event of sortedEvents) {
    const interval = getEventScheduledInterval(event);
    const startsAfterRemovedSlot =
      interval != null && interval.start.valueOf() >= gapEndMs;

    let belongsToRemovedWindow: boolean;
    if (
      templateWindowsById != null &&
      columnDate != null &&
      removedWindow != null
    ) {
      belongsToRemovedWindow = eventBelongsToTemplateWindow(
        event,
        removedWindow,
        templateWindowsById
      );
    } else {
      belongsToRemovedWindow = true;
    }

    if (!startsAfterRemovedSlot || !belongsToRemovedWindow) {
      unchanged.push(event);
    } else {
      toCompact.push(event);
    }
  }
  // Early exit if there are no cards following the removed card
  if (toCompact.length === 0) return events;

  let cursor = gapStart.clone();
  // If window-scoped compaction is needed, adjust the cursor to the start of the effective window
  if (useWindowScopedCompaction && columnDate && removedWindow) {
    const [effectiveWindow] = getEffectiveWindows(
      [removedWindow],
      columnDate,
      now
    );
    if (!effectiveWindow) {
      return events;
    }

    const gapStartMinutes = momentToClockMinutes(gapStart);
    if (gapStartMinutes < effectiveWindow.startMinutes) {
      cursor = getDate(columnDate)
        .startOf("day")
        .add(effectiveWindow.startMinutes, "minutes");
    }
  }

  const compacted: PrioritySchedulerColumnEvent[] = [];
  for (let index = 0; index < toCompact.length; index++) {
    const event = toCompact[index];
    const duration = event.expectedDuration ?? DEFAULT_EVENT_DURATION_MINUTES;
    const newStart = cursor.clone();
    const newEnd = newStart.clone().add(duration, "minutes");

    if (
      useWindowScopedCompaction &&
      columnDate &&
      removedWindow &&
      !intervalFitsEffectiveTemplateWindow(
        newStart,
        newEnd,
        removedWindow,
        columnDate,
        now
      )
    ) {
      compacted.push(event, ...toCompact.slice(index + 1));
      break;
    }

    compacted.push({
      ...event,
      scheduledStartDate: getDateISO(newStart),
      scheduledEndDate: getDateISO(newEnd),
    });
    cursor = newEnd.clone();
  }

  return [...unchanged, ...compacted];
};

export const findOverlappingAttemptIds = (
  events: PrioritySchedulerColumnEvent[],
  cardId: string,
  startISO: string,
  endISO: string
): string[] => {
  const rangeStart = getDate(startISO);
  const rangeEnd = getDate(endISO);

  return events
    .filter((ev) => ev.id !== cardId)
    .map((ev) => {
      const bounds = getEventScheduledInterval(ev);
      if (!bounds) return null;
      return intervalsOverlapMoments(
        rangeStart,
        rangeEnd,
        bounds.start,
        bounds.end
      )
        ? ev.id
        : null;
    })
    .filter((id): id is string => id !== null);
};

export const buildAffectedCardsForColumn = (
  column: IPrioritySchedulerColumnData,
  dayStartTime: string | undefined,
  fallbackDate: Moment
): PrioritySchedulerAffectedCard[] => {
  const events = (column.events ?? []).filter(
    (event) => !isWorkOrderDragDisabled(event)
  );
  if (events.length === 0) return [];

  const baseDate = column.date ?? fallbackDate;
  const startMoment = dayStartTime
    ? setTime(baseDate, dayStartTime)
    : getDate(baseDate).startOf("day");

  if (events.every((e) => e.scheduledStartDate)) {
    return events.map((event) => {
      const duration = event.expectedDuration ?? DEFAULT_EVENT_DURATION_MINUTES;
      const start = getDate(event.scheduledStartDate);
      const end = event.scheduledEndDate
        ? getDate(event.scheduledEndDate)
        : start.clone().add(duration, "minutes");

      return {
        attemptId: event.id,
        scheduledStartDate: getDateISO(start),
        scheduledEndDate: getDateISO(end),
        userId: column.user?.id ?? event.assignedUserId,
      };
    });
  }

  const sortedEvents = [...events].sort((a, b) => {
    const ta = a.scheduledStartDate
      ? getDate(a.scheduledStartDate).valueOf()
      : Number.MAX_SAFE_INTEGER;
    const tb = b.scheduledStartDate
      ? getDate(b.scheduledStartDate).valueOf()
      : Number.MAX_SAFE_INTEGER;
    if (ta !== tb) return ta - tb;

    return 0;
  });

  let cursor = startMoment.clone();

  return sortedEvents.map((event) => {
    const duration = event.expectedDuration ?? DEFAULT_EVENT_DURATION_MINUTES;
    const start = event.scheduledStartDate
      ? getDate(event.scheduledStartDate)
      : cursor.clone();
    const end = event.scheduledEndDate
      ? getDate(event.scheduledEndDate)
      : start.clone().add(duration, "minutes");
    cursor = end.clone();

    return {
      attemptId: event.id,
      scheduledStartDate: getDateISO(start),
      scheduledEndDate: getDateISO(end),
      userId: column.user?.id ?? event.assignedUserId,
    };
  });
};

/**
 * Converts a moment to a vertical pixel offset inside the column card container,
 * measured from the column's scheduling start (day start time on that date).
 */
const momentToOffsetPx = (
  m: Moment,
  startMoment: Moment,
  hourHeight: number
): number => {
  const minutes = m.diff(startMoment, "minutes", true);
  return (minutes / 60) * hourHeight;
};

/**
 * Returns the Y position (in pixels) of the "now" line inside a column's card
 * container — the same coordinate space used by {@link DraggingLineIndicator}
 * and drop scheduling.
 *
 * This is relative to day start (e.g. 07:00), not midnight, so it lines up with
 * painted cards and the red {@link CurrentTimeIndicator} when viewed in context.
 *
 * @example
 * // Day starts at 07:00, hourHeight = 60px/hour, now is 09:30
 * getCurrentTimeOffsetPx({
 *   columnDate: moment('2026-05-26'),
 *   dayStartTime: '07:00',
 *   hourHeight: 60,
 *   now: moment('2026-05-26 09:30'),
 * });
 * // => 150  (2.5 hours × 60px)
 */
export function getCurrentTimeOffsetPx({
  columnDate,
  dayStartTime,
  hourHeight,
  now = moment(),
}: {
  columnDate: Moment | undefined;
  dayStartTime?: string;
  hourHeight: number;
  now?: Moment;
}): number {
  if (!columnDate) {
    const [startH = 0, startM = 0] = (dayStartTime ?? "00:00")
      .split(":")
      .map(Number);
    const dayStartMinutes = startH * 60 + startM;
    const nowMinutes = now.hours() * 60 + now.minutes();
    return Math.max(0, ((nowMinutes - dayStartMinutes) / 60) * hourHeight);
  }

  const startMoment = dayStartTime
    ? setTime(columnDate, dayStartTime)
    : getDate(columnDate).startOf("day");

  return Math.max(0, momentToOffsetPx(now, startMoment, hourHeight));
}

/**
 * Builds every valid drop anchor for a column: where the blue drag line may
 * snap and where a dropped card may be scheduled.
 *
 * Anchors are always in top-to-bottom order of usefulness:
 * 1. Current time — empty column or upper region (drop right after "now")
 * 2. Bottom of each card before the last — insert after that card
 * 3. Just below the last card — append to the end of the column
 * 4. Disabled-slot ends — first legal position after each unavailable range
 *
 * Anchors that land on/inside a disabled slot are pushed to that slot's bottom
 * so empty columns and gaps without events cannot schedule into disabled ranges.
 *
 * @example
 * // Two cards stacked in the column; now line at 150px
 * const layouts = [
 *   { top: 200, height: 60 },  // card A
 *   { top: 300, height: 60 },  // card B
 * ];
 * getColumnSnapPositions(layouts, 150);
 * // => [150, 260, 360]
 * //     ^now  ^after A        ^after B
 */
export function getColumnSnapPositions(
  sortedLayouts: PrioritySchedulerEventLayout[],
  currentTimeOffsetPx: number,
  disabledSlotRects: DisabledSlotRect[] = []
): number[] {
  const snapPositions: number[] = [];

  if (currentTimeOffsetPx > 0) {
    snapPositions.push(currentTimeOffsetPx);
  }

  for (let i = 0; i < sortedLayouts.length - 1; i++) {
    const currentBottom = sortedLayouts[i].top + sortedLayouts[i].height;
    snapPositions.push(currentBottom);
  }

  if (sortedLayouts.length > 0) {
    const last = sortedLayouts[sortedLayouts.length - 1];
    snapPositions.push(last.top + last.height);
  }

  for (const slot of disabledSlotRects) {
    if (slot.bottom > 0) {
      snapPositions.push(slot.bottom);
    }
  }

  return snapPositions.map((position) =>
    snapPastDisabledSlots(position, disabledSlotRects)
  );
}

/**
 * Picks the snap anchor closest to where the user is dragging vertically.
 *
 * Used for both the visual line during drag and the final `dropOffsetY` on
 * drop commit so indicator and schedule stay in sync.
 *
 * @example
 * // User's pointer is at 275px; anchors from getColumnSnapPositions above
 * resolveSnappedDropY(275, [150, 280, 361]);
 * // => 280  (closest to the between-cards midpoint)
 *
 * @example
 * // Empty column: only anchor is current time
 * resolveSnappedDropY(400, [150]);
 * // => 150
 *
 * @example
 * // No anchors (e.g. before day start) — use raw pointer Y
 * resolveSnappedDropY(50, []);
 * // => 50
 */
export function resolveSnappedDropY(
  dragY: number,
  snapPositions: number[]
): number {
  if (snapPositions.length === 0) {
    return dragY;
  }

  let closest = snapPositions[0];
  let minDist = Math.abs(dragY - closest);
  for (let i = 1; i < snapPositions.length; i++) {
    const dist = Math.abs(dragY - snapPositions[i]);
    if (dist < minDist) {
      minDist = dist;
      closest = snapPositions[i];
    }
  }

  return closest;
}

export type DragIndicatorLiveState = {
  columnId: string;
  rawOffsetY: number;
};

export function resolveDragIndicatorRawPosition(
  translated: { left: number; top: number; width: number },
  columnElements: Map<string, HTMLElement>
): DragIndicatorLiveState | null {
  const pointerX = translated.left + translated.width / 2;

  for (const [colId, colEl] of columnElements.entries()) {
    const rect = colEl.getBoundingClientRect();
    if (pointerX >= rect.left && pointerX <= rect.right) {
      const rawOffsetY = Math.max(
        0,
        Math.min(translated.top - rect.top, rect.height)
      );
      return { columnId: colId, rawOffsetY };
    }
  }

  return null;
}

export function computeDragIndicatorSnappedY({
  rawY,
  destColumn,
  draggedCard,
  calculatedHourHeight,
  dayStartTime,
  dayEndTime,
  fallbackDate,
  workTemplateBases,
}: {
  rawY: number;
  destColumn: IPrioritySchedulerColumnData | undefined;
  draggedCard: PrioritySchedulerColumnEvent | undefined;
  calculatedHourHeight: number;
  dayStartTime?: string;
  dayEndTime?: string;
  fallbackDate: Moment;
  workTemplateBases?: ICalendarWorkTemplateBase[];
}): number | null {
  if (!draggedCard) {
    return null;
  }

  const draggedCardId = draggedCard.id;
  const excludeDraggedCard =
    !!draggedCardId &&
    (destColumn?.events ?? []).some((event) => event.id === draggedCardId);

  return resolveSnappedColumnDropOffsetY({
    rawY,
    destColumn,
    draggedCardId,
    excludeDraggedCard,
    calculatedHourHeight,
    dayStartTime,
    dayEndTime,
    fallbackDate,
    workTemplateBases,
    draggedWorkOrderTemplateId: draggedCard.workOrderTemplateId,
    expectedDurationMinutes:
      draggedCard.expectedDuration ?? DEFAULT_EVENT_DURATION_MINUTES,
  });
}

export const getCardData = (data: unknown) => {
  const current = data as PrioritySchedulerCardDragData | undefined;
  return current?.type === "card" ? current : undefined;
};

export const getContainerData = (data: unknown) => {
  const current = data as PrioritySchedulerContainerDragData | undefined;
  return current?.type === "container" ? current : undefined;
};

export const resolveDropLocation = (
  overId: string,
  overData: unknown
): { location: PrioritySchedulerLocation; overCardId?: string } | null => {
  const overCardData = getCardData(overData);
  if (overCardData) {
    return {
      location: overCardData.container,
      overCardId: overCardData.cardId,
    };
  }

  const overContainerData = getContainerData(overData);
  if (overContainerData) {
    return {
      location: overContainerData.container,
    };
  }

  if (overId.startsWith(`${COLUMN_CONTAINER_PREFIX}:`)) {
    return {
      location: { type: "column", id: getRawId(overId) },
    };
  }

  if (overId.startsWith(`${TRAY_CONTAINER_PREFIX}:`)) {
    return {
      location: { type: "tray", id: getRawId(overId) },
    };
  }

  return null;
};

export type DragEndDropContext = {
  activeCardData: PrioritySchedulerCardDragData;
  resolvedDrop: {
    location: PrioritySchedulerLocation;
    overCardId?: string;
  };
};

/** Returns null when the drop should be ignored (invalid target, same card, same column, etc.). */
export function parseDragEndDrop(
  event: DragEndEvent
): DragEndDropContext | null {
  const { active, over } = event;
  if (!over) {
    return null;
  }

  const activeCardData = getCardData(active.data.current);
  if (!activeCardData) {
    return null;
  }

  const resolvedDrop = resolveDropLocation(String(over.id), over.data.current);
  if (!resolvedDrop) {
    return null;
  }

  if (resolvedDrop.overCardId === activeCardData.cardId) {
    return null;
  }

  if (resolvedDrop.location.type !== "column") {
    return null;
  }

  const isSameContainer =
    resolvedDrop.location.id === activeCardData.container.id &&
    resolvedDrop.location.type === activeCardData.container.type;
  if (isSameContainer) {
    return null;
  }

  return { activeCardData, resolvedDrop };
}

export function buildDragMovePayload(
  context: DragEndDropContext,
  dropOffsetY?: number
): PrioritySchedulerMovePayload {
  const { activeCardData, resolvedDrop } = context;
  return {
    cardId: activeCardData.cardId,
    from: activeCardData.container,
    to: resolvedDrop.location,
    overCardId: resolvedDrop.overCardId,
    ...(dropOffsetY !== undefined ? { dropOffsetY } : {}),
  };
}

export type ComputeColumnDropOffsetYParams = {
  active: DragEndEvent["active"];
  context: DragEndDropContext;
  dragIndicatorState: DragIndicatorLiveState | null;
  columnElements: Map<string, HTMLElement>;
  localColumns: IPrioritySchedulerColumnData[];
  eventLookupById: Map<string, PrioritySchedulerColumnEvent>;
  calculatedHourHeight: number;
  dayStartTime?: string;
  dayEndTime?: string;
  fallbackDate: Moment;
  workTemplateBases: ICalendarWorkTemplateBase[];
};

export function computeColumnDropOffsetY({
  active,
  context,
  dragIndicatorState,
  columnElements,
  localColumns,
  eventLookupById,
  calculatedHourHeight,
  dayStartTime,
  dayEndTime,
  fallbackDate,
  workTemplateBases,
}: ComputeColumnDropOffsetYParams): number | undefined | null {
  const { activeCardData, resolvedDrop } = context;
  const destColumnId = resolvedDrop.location.id;

  if (!active.rect.current.translated) {
    return undefined;
  }

  const columnEl = columnElements.get(destColumnId);
  if (!columnEl) {
    return undefined;
  }

  const { translated } = active.rect.current;
  let rawY: number;

  if (dragIndicatorState?.columnId === destColumnId) {
    rawY = dragIndicatorState.rawOffsetY;
  } else {
    const columnRect = columnEl.getBoundingClientRect();
    rawY = translated.top - columnRect.top;
    const movedPreview = eventLookupById.get(activeCardData.cardId);
    const durationMinutes =
      movedPreview?.expectedDuration ?? DEFAULT_EVENT_DURATION_MINUTES;
    const cardHeight = (durationMinutes / 60) * calculatedHourHeight;
    const maxY = Math.max(0, columnEl.clientHeight - cardHeight);
    rawY = Math.max(0, Math.min(rawY, maxY));
  }

  const destColumn = localColumns.find((col) => col.id === destColumnId);
  const excludeDraggedCard =
    activeCardData.container.type === "column" &&
    activeCardData.container.id === destColumnId;
  const draggedCard = eventLookupById.get(activeCardData.cardId);

  return resolveSnappedColumnDropOffsetY({
    rawY,
    destColumn,
    draggedCardId: activeCardData.cardId,
    excludeDraggedCard,
    calculatedHourHeight,
    dayStartTime,
    dayEndTime,
    fallbackDate,
    workTemplateBases,
    draggedWorkOrderTemplateId: draggedCard?.workOrderTemplateId,
    expectedDurationMinutes:
      draggedCard?.expectedDuration ?? DEFAULT_EVENT_DURATION_MINUTES,
  });
}

export type MoveCardForDragEndResult = {
  next: IPrioritySchedulerColumnData[];
  moved?: PrioritySchedulerColumnEvent;
};

export function moveCardForDragEnd(
  localColumns: IPrioritySchedulerColumnData[],
  context: DragEndDropContext,
  eventLookupById: Map<string, PrioritySchedulerColumnEvent>
): MoveCardForDragEndResult {
  const { activeCardData, resolvedDrop } = context;
  const destColumnId = resolvedDrop.location.id;

  if (activeCardData.container.type === "column") {
    return moveCardBetweenColumns(
      localColumns,
      activeCardData.container.id,
      destColumnId,
      activeCardData.cardId
    );
  }

  if (activeCardData.container.type === "tray") {
    const card = eventLookupById.get(activeCardData.cardId);
    if (!card) {
      return { next: localColumns };
    }

    const result = addCardToColumn(localColumns, destColumnId, card);
    if (!result) {
      return { next: localColumns };
    }

    return { next: result.next, moved: result.added };
  }

  return { next: localColumns };
}

export type ApplyDragEndColumnUpdatesResult =
  | {
      kind: "applied";
      patchedNext: IPrioritySchedulerColumnData[];
      affectedCards: PrioritySchedulerAffectedCard[];
      overlappingAttemptIds?: string[];
    }
  | { kind: "window-rejected" };

export type ApplyDragEndColumnUpdatesParams = {
  moved: PrioritySchedulerColumnEvent;
  next: IPrioritySchedulerColumnData[];
  context: DragEndDropContext;
  dropOffsetY?: number;
  calculatedHourHeight: number;
  dayStartTime?: string;
  fallbackDate: Moment;
  workTemplateBases: ICalendarWorkTemplateBase[];
};

export function applyDragEndColumnUpdates({
  moved,
  next,
  context,
  dropOffsetY,
  calculatedHourHeight,
  dayStartTime,
  fallbackDate,
  workTemplateBases,
}: ApplyDragEndColumnUpdatesParams): ApplyDragEndColumnUpdatesResult {
  const { activeCardData, resolvedDrop } = context;
  const destColumnId = resolvedDrop.location.id;
  const toColumn = next.find((column) => column.id === destColumnId);

  let patchedNext = next;
  let overlappingAttemptIds: string[] | undefined;

  if (dropOffsetY !== undefined) {
    const schedule = computeScheduleFromDropY({
      dropOffsetY,
      hourHeight: calculatedHourHeight,
      columnDate: toColumn?.date,
      fallbackDate,
      dayStartTime,
      expectedDurationMinutes:
        moved.expectedDuration ?? DEFAULT_EVENT_DURATION_MINUTES,
    });

    if (schedule) {
      const tentativeStart = getDate(schedule.scheduledStartDate);

      if (
        !isDropWithinMatchingWindow({
          workOrderTemplateId: moved.workOrderTemplateId,
          scheduledStart: tentativeStart,
          workTemplateBases,
        })
      ) {
        return { kind: "window-rejected" };
      }

      const movedWithUser: PrioritySchedulerColumnEvent = {
        ...moved,
        ...(toColumn?.user?.id ? { assignedUserId: toColumn.user.id } : {}),
        scheduledStartDate: schedule.scheduledStartDate,
        scheduledEndDate: schedule.scheduledEndDate,
      };

      const destEvents = (toColumn?.events ?? []).map((event) =>
        event.id === movedWithUser.id ? movedWithUser : event
      );

      patchedNext = next.map((column) =>
        column.id !== destColumnId ? column : { ...column, events: destEvents }
      );

      overlappingAttemptIds = findOverlappingAttemptIds(
        destEvents,
        movedWithUser.id,
        schedule.scheduledStartDate,
        schedule.scheduledEndDate
      );
    }
  }

  const affectedColumnIds = new Set<string>([destColumnId]);
  if (activeCardData.container.type === "column") {
    affectedColumnIds.add(activeCardData.container.id);
  }

  if (
    activeCardData.container.type === "column" &&
    activeCardData.container.id !== destColumnId
  ) {
    const originColumnId = activeCardData.container.id;
    patchedNext = patchedNext.map((column) => {
      if (column.id !== originColumnId) return column;
      return {
        ...column,
        events: compactOriginColumnAfterRemoval(column.events ?? [], moved, {
          columnDate: column.date ?? fallbackDate,
          workTemplateBases,
        }),
      };
    });
  }

  patchedNext = sortColumnEventsBySchedule(patchedNext, affectedColumnIds);

  const affectedCards = patchedNext
    .filter((column) => affectedColumnIds.has(column.id))
    .flatMap((column) =>
      buildAffectedCardsForColumn(column, dayStartTime, fallbackDate)
    );

  return {
    kind: "applied",
    patchedNext,
    affectedCards,
    ...(overlappingAttemptIds?.length ? { overlappingAttemptIds } : {}),
  };
}

export function buildAfterCardDropPayload(
  movePayload: PrioritySchedulerMovePayload,
  columns: IPrioritySchedulerColumnData[],
  affectedCards: PrioritySchedulerAffectedCard[],
  overlappingAttemptIds?: string[]
): PrioritySchedulerAfterCardDropPayload {
  return {
    ...movePayload,
    dropPosition: "end",
    affectedCards,
    columns,
    ...(overlappingAttemptIds?.length ? { overlappingAttemptIds } : {}),
  };
}
